/**
 * Purge API Service
 *
 * Domain-specific service for FI Data Purge endpoints.
 * Uses httpClient for consistent interceptor handling (token rotation, error handling).
 *
 * NOTE: Currently using mock implementations for development.
 * Replace with actual API calls when backend is ready.
 */

import { httpClient } from './http-client.service';
import type {
  PurgeSummaryParams,
  PurgeStatisticsParams,
  PurgeStatisticsResponse,
  DataPurgingStatsAPIResponse,
  DataPurgingStatsData,
  DataPurgingSummaryAPIResponse,
  DataPurgingSummaryData,
  DownloadReportResponse,
  DownloadReportAPIResponse,
} from '../types/purge';

/**
 * Mock data generator for statistics (no longer used for selected range, kept for backward compatibility)
 */
const generateMockStatistics = (params: PurgeStatisticsParams): PurgeStatisticsResponse => {
  const lifetimeTotal = 1209280; // Fixed lifetime total from screenshot
  const selectedRangeTotal = params.start_date && params.end_date
    ? Math.floor(Math.random() * 200000) + 1100000 // Random between 1.1M - 1.3M
    : lifetimeTotal;

  return {
    lifetime_total: lifetimeTotal,
    selected_range_total: selectedRangeTotal,
    oldest_purge_date: '2024-01-01',
    latest_purge_date: new Date().toISOString().split('T')[0],
  };
};

class PurgeAPIService {
  /**
   * Fetch purge summary data (daily or monthly aggregation)
   *
   * Uses the real API endpoint: GET /data-purging/summary
   * Returns unwrapped data to match the frontend expectations
   *
   * @param params - Date range and aggregation level
   * @returns Promise with purge summary data
   */
  async getPurgeSummary(params: PurgeSummaryParams): Promise<DataPurgingSummaryData> {
    const response = await httpClient.get<DataPurgingSummaryAPIResponse>('data-purging/summary', {
      params: {
        period: params.period,
        start_date: params.start_date,
        end_date: params.end_date,
      },
    });
    // Unwrap twice: AxiosResponse.data.data
    return response.data.data;
  }

  /**
   * Fetch purge statistics (lifetime and selected range totals)
   *
   * @param params - Optional date range for selected range calculation
   * @returns Promise with statistics data
   */
  async getPurgeStatistics(params: PurgeStatisticsParams = {}): Promise<PurgeStatisticsResponse> {
    // TODO: Replace with actual API call when backend is ready
    // const response = await httpClient.get<PurgeStatisticsResponse>('api/v1/purge-statistics', {
    //   params,
    // });
    // return response.data;

    // Mock implementation with delay
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(generateMockStatistics(params));
      }, 300);
    });
  }

  /**
   * Download purge report for a specific period
   *
   * Calls the backend to get a presigned S3 URL for the report.
   * The backend checks S3 for the report file and returns a 30-minute presigned URL.
   *
   * @param period - Period string in YYYY-MM format (e.g., "2024-11")
   * @returns Promise with download URL and file info
   * @throws Error if report not found (404) or server error
   */
  async downloadReport(period: string): Promise<DownloadReportResponse> {
    const response = await httpClient.get<DownloadReportAPIResponse>('data-purging/download', {
      params: { period },
    });
    // Unwrap: AxiosResponse.data.data
    return response.data.data;
  }

  /**
   * Fetch lifetime purge statistics from dedicated endpoint
   * This provides the total count of all data purged across all time
   *
   * Uses the real API endpoint: GET /data-purging/stats
   * Returns unwrapped data to match the purge-api pattern
   *
   * @returns Promise with lifetime purge statistics data
   */
  async getLifetimePurgeStats(): Promise<DataPurgingStatsData> {
    const response = await httpClient.get<DataPurgingStatsAPIResponse>('data-purging/stats');
    // Unwrap twice: AxiosResponse.data.data
    return response.data.data;
  }
}

// Export singleton instance
export const purgeAPI = new PurgeAPIService();
